import { useMemo, useState } from "react";
import { useGetTask, Task, TaskInstance, TaskHistory, InstanceStatus, useReassignInstance } from "@/lib/api/checklist";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PriorityBadge, RecurrenceDisplay, StatusIndicator } from "./components";
import { format } from "date-fns";
import { CommonDestinationTimezone } from "@/lib/utils";
import moment from "moment-timezone";
import { Skeleton } from "@/components/ui/skeleton";
import { Clock, Edit, Trash2, Users, ArrowRightLeftIcon, FileText } from "lucide-react";
import { DataTable, type DataTableColumn } from "@/components/ui/DataTable/index";
import { DatePicker } from "@/components/ui/date_picker";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Combobox } from "@/components/ui/combobox";
import { useListMinimalUsers } from "@/lib/api/users";
import { toast } from "@/components/ui/use-toast";
import { MetaCustomerInfoPreview } from "./templates/MetaCustomerInfoPreview";
import { doesInstanceMetaHaveCustomerInfo, generateTriggerMetaDataColumns } from "./templates/schemas";

interface TaskDetailModalProps {
  open: boolean;
  onClose: () => void;
  taskId: string;
  onEdit: (task: Task) => void;
  onDelete: (task: Task) => void;
  onCreateInstances: (task: Task) => void;
}

export function TaskDetailModal({
  open,
  onClose,
  taskId,
  onEdit,
  onDelete,
  onCreateInstances,
}: TaskDetailModalProps) {
  const taskResult = useGetTask(taskId);
  const isLoading = taskResult.isLoading;
  const task = taskResult?.data?.data;

  const [statusFilter, setStatusFilter] = useState<"all" | "pending" | "completed">("pending");
  const [showLateOnly, setShowLateOnly] = useState(false);
  const [endDate, setEndDate] = useState<Date | undefined>();
  const [reassignInstanceId, setReassignInstanceId] = useState<string | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string>("");

  const minimalUserListResult = useListMinimalUsers();
  const users = (minimalUserListResult?.data?.data || []);

  const formatDate = (dateString: string) => {
    return moment.tz(dateString, CommonDestinationTimezone).format("MMM DD, YYYY HH:mm a");
  };

  const reassignInstance = useReassignInstance(reassignInstanceId || "");

  const handleReassign = async () => {
    if (!reassignInstanceId || !selectedUserId) return;

    try {
      const result = await reassignInstance.mutateAsync({ assigned_to_id: selectedUserId });
      if (!result.success) {
        throw result;
      }
      toast({
        title: "Success",
        description: "Task instance reassigned successfully",
      });
      setReassignInstanceId(null);
      setSelectedUserId("");
    } catch (error: any) {
      toast({
        title: "Error",
        description: "Failed to reassign task instance",
        variant: "destructive",
      });
    }
  };

  // Filter + sort instances (memoized so DataTable's `data` reference is stable
  // between unrelated re-renders — e.g. typing in the reassign dialog).
  const sortedInstances = useMemo(() => {
    const filtered = (task?.instances || []).filter((instance: TaskInstance) => {
      if (statusFilter === "pending" && instance.status !== InstanceStatus.PENDING) return false;
      if (statusFilter === "completed" && instance.status !== InstanceStatus.COMPLETED) return false;
      if (showLateOnly && !instance.is_late) return false;
      if (endDate) {
        const dueDate = new Date(instance.due_date);
        if (dueDate > endDate) return false;
      }
      return true;
    });

    return filtered.slice().sort((a: TaskInstance, b: TaskInstance) => {
      // ISO 8601 strings sort lexicographically — no moment allocation needed.
      const aDue = a.due_date || "";
      const bDue = b.due_date || "";
      let dateSort = 0;
      if (aDue < bDue) dateSort = -1;
      else if (aDue > bDue) dateSort = 1;
      if (statusFilter === "completed") dateSort = -dateSort; // descending for completed
      if (dateSort !== 0) return dateSort;
      return a.id.localeCompare(b.id);
    });
  }, [task?.instances, statusFilter, showLateOnly, endDate]);

  const trigger_type: string | undefined = sortedInstances?.[0]?.template_instance?.trigger_meta_data?.trigger_type;

  const columns = useMemo(() => {
    const columns: DataTableColumn<TaskInstance>[] = [
      {
        accessorKey: "status",
        header: "Status",
        cell: (info) => <StatusIndicator status={info.getValue() as InstanceStatus} secondary_status={info.row.original.secondary_status}/>,
        size: 100,
      },
      {
        accessorKey: "assigned_to.name",
        header: "Assigned To",
        cell: ({ row }) => (
          <span className="font-medium">
            {row.original.assigned_to?.name || "Unassigned"}
          </span>
        ),
      },
      {
        accessorKey: "triggered_by_user",
        header: "Triggered By",
        accessorFn: (row: any) => row.template_instance?.triggered_by_user?.name,
        cell: (info) => {
          return info.getValue() ?? "-";
        },
        size: 120,
      },
      {
        id: "actions",
        header: "Actions",
        cell: ({ row }) => {
          const instance = row.original;
          return (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setReassignInstanceId(instance.id);
                setSelectedUserId(instance.assigned_to_id || "");
              }}
              disabled={instance.status === InstanceStatus.COMPLETED}
              title="Reassign"
            >
              <ArrowRightLeftIcon className="h-4 w-4" />
            </Button>
          );
        },
        size: 100,
      },
      {
        accessorKey: "due_date",
        header: "Due Date",
        columnFilterType: 'date',
        filterDateTimezone: CommonDestinationTimezone,
        filterDisplayFn: (v: unknown) => v ? moment.tz(v as string, CommonDestinationTimezone).format("MMM DD, YYYY HH:mm a") : "-",
        filterParseDateFn: (v: unknown) => v ? moment.tz(v as string, CommonDestinationTimezone) : null,
        cell: ({ row }) => (
          <span className="text-sm">
            {formatDate(row.original.due_date)}
          </span>
        ),
      },
      {
        accessorKey: "completed_at",
        header: "Completed At",
        columnFilterType: 'date',
        filterDateTimezone: CommonDestinationTimezone,
        filterDisplayFn: (v: unknown) => v ? moment.tz(v as string, CommonDestinationTimezone).format("MMM DD, YYYY HH:mm a") : "-",
        filterParseDateFn: (v: unknown) => v ? moment.tz(v as string, CommonDestinationTimezone) : null,
        cell: ({ row }) => (
          <span className="text-sm">
            {row.original.completed_at ? formatDate(row.original.completed_at) : "-"}
          </span>
        ),
      },
      {
        accessorKey: "is_late",
        header: "Status",
        enableColumnFilter: false,
        cell: ({ row }) => {
          if (row.original.is_late) {
            if (row.original.status === InstanceStatus.COMPLETED) {
              return (
                <Badge variant="outline" className="text-xs border-destructive text-destructive">
                  Completed Late
                </Badge>
              );
            }
            return <Badge variant="destructive" className="text-xs">Overdue</Badge>;
          }
          return null;
        },
      },
      ...(generateTriggerMetaDataColumns(trigger_type) as any)
    ];
    return columns;
  }, [trigger_type]);


  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-[96vw] max-h-[90vh] overflow-y-auto">
        <DialogHeader className="space-y-3">
          <DialogTitle className="text-xl">Task Details</DialogTitle>
          <DialogDescription className="text-base">
            View task information, instances, and history
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        ) : task ? (
          <div className="flex-1 min-h-0 flex flex-col mt-4">
            <div className="flex flex-col gap-6 px-1 h-full">
              {/* Task Information */}
              <div className="space-y-4">
                <div className="flex items-start justify-between">
                  <div className="space-y-2 flex-1">
                    <h3 className="text-2xl font-semibold">{task.title}</h3>
                    {task.description && (
                      <p className="text-base text-muted-foreground">
                        {task.description}
                      </p>
                    )}
                    {task.note && (
                      <div className="mt-3 p-3 bg-muted/10 rounded-md border border-border">
                        <p className="text-sm font-medium text-muted-foreground mb-1">Note:</p>
                        <p className="text-sm whitespace-pre-wrap">{task.note}</p>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2 flex-shrink-0 ml-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onEdit(task)}
                    >
                      <Edit className="h-4 w-4" />
                      <span className="ml-1.5">Edit</span>
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onCreateInstances(task)}
                    >
                      <Users className="h-4 w-4" />
                      <span className="ml-1.5">Assign</span>
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onDelete(task)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                      <span className="ml-1.5">Delete</span>
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 p-4 bg-muted/20 rounded-lg border border-border">
                  <div className="space-y-1">
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Status</span>
                    <div>
                      <Badge variant={task.is_active ? "default" : "secondary"} className="font-medium">
                        {task.is_active ? "Active" : "Inactive"}
                      </Badge>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Recurrence</span>
                    <div>
                      <RecurrenceDisplay
                        pattern={task.recurrence_pattern}
                        interval={task.recurrence_interval}
                        calculationType={task.recurrence_calculation_type}
                      />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Created</span>
                    <div className="text-sm font-medium">
                      {formatDate(task.created_at)}
                    </div>
                  </div>
                </div>
              </div>

              {sortedInstances?.length === 1 &&
                sortedInstances[0].template_instance?.trigger_meta_data &&
                doesInstanceMetaHaveCustomerInfo(sortedInstances[0].template_instance?.trigger_meta_data) && (
                  <MetaCustomerInfoPreview instance={sortedInstances[0].template_instance.trigger_meta_data} />
                )}

              {/* Tabs for Instances and History */}
              <Tabs defaultValue="instances" className="w-full flex-1 flex flex-col">
                <TabsList className="grid w-full grid-cols-2 h-11">
                  <TabsTrigger value="instances" className="text-sm">
                    Assigned Out ({sortedInstances?.length})
                  </TabsTrigger>
                  <TabsTrigger value="history" className="text-sm">
                    Task History ({task.histories.length})
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="instances" className="mt-6 flex-1 min-h-0 flex flex-col">
                  <div className="flex-1 min-h-0 flex flex-col gap-4">
                    {/* Filters */}
                    <div className="flex flex-wrap gap-4 items-center flex-shrink-0">
                      <div className="flex gap-2">
                        <Button
                          variant={statusFilter === "all" ? "default" : "outline"}
                          size="sm"
                          onClick={() => setStatusFilter("all")}
                        >
                          All
                        </Button>
                        <Button
                          variant={statusFilter === "pending" ? "default" : "outline"}
                          size="sm"
                          onClick={() => setStatusFilter("pending")}
                        >
                          Pending
                        </Button>
                        <Button
                          variant={statusFilter === "completed" ? "default" : "outline"}
                          size="sm"
                          onClick={() => setStatusFilter("completed")}
                        >
                          Completed
                        </Button>
                      </div>

                      <div className="flex items-center space-x-2">
                        <Switch
                          id="late-only"
                          checked={showLateOnly}
                          onCheckedChange={setShowLateOnly}
                        />
                        <Label htmlFor="late-only" className="text-sm">
                          Late Only
                        </Label>
                      </div>

                      <DatePicker
                        value={endDate}
                        onChange={setEndDate}
                        placeholder="Filter by end date"
                        buttonClassName="w-[200px]"
                      />
                    </div>

                    {/* Table */}
                    {sortedInstances.length > 0 ? (
                      <DataTable
                        columns={columns as any[]}
                        data={sortedInstances}
                        stickyHeaders
                        enableSorting
                        enableMultiSort
                        enableFiltering
                        enableGrouping
                        enableColumnVisibility
                        enableVirtualization
                        virtualizationRowHeight={52}
                        virtualizationOverscan={2}
                        defaultColumnVisibility={sortedInstances?.length === 1 ? ({
                          template_instance_2: false,
                          template_instance_3: false
                        }) : undefined}
                        filterStyle='select'
                        filterMode='internal'
                        emptyMessage={isLoading ? "Loading..." : "No data available"}
                      />
                    ) : (
                      <div className="text-center py-12 text-muted-foreground">
                        <Users className="h-12 w-12 mx-auto mb-3 opacity-20" />
                        <p>No instances match the selected filters</p>
                      </div>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="history" className="mt-6">
                  <div className="space-y-3 max-h-[400px] overflow-y-auto">
                    {task.histories.map((entry: TaskHistory) => (
                      <div
                        key={entry.id}
                        className="flex items-start gap-4 p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                      >
                        <Clock className="h-4 w-4 mt-0.5 text-muted-foreground flex-shrink-0" />
                        <div className="flex-1 space-y-1">
                          <p className="text-sm">
                            <span className="font-medium">
                              {entry.changed_by?.name}
                            </span>{" "}
                            <span className="text-muted-foreground">{entry.change_type}</span>
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {formatDate(entry.created_at)}
                          </p>
                        </div>
                      </div>
                    ))}
                    {task.histories.length === 0 && (
                      <div className="text-center py-12 text-muted-foreground">
                        <Clock className="h-12 w-12 mx-auto mb-3 opacity-20" />
                        <p>No history available</p>
                      </div>
                    )}
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            Task not found
          </div>
        )}
      </DialogContent>

      {/* Reassign Dialog */}
      <Dialog open={!!reassignInstanceId} onOpenChange={() => {
        setReassignInstanceId(null);
        setSelectedUserId("");
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reassign Task Instance</DialogTitle>
            <DialogDescription>
              Select a new person to assign this task instance to.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Combobox
              value={selectedUserId}
              onValueChange={(value: string) => setSelectedUserId(value)}
              options={users.map((user: any) => ({
                value: user.id,
                label: user.name
              }))}
              placeholder="Select a user"
              dwimOnChange
              isModal
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setReassignInstanceId(null);
                setSelectedUserId("");
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleReassign}
              disabled={!selectedUserId || reassignInstance.isLoading}
            >
              {reassignInstance.isLoading ? "Reassigning..." : "Reassign"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
